import React from "react";
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  TextField,
  Stack,
  Checkbox,
} from "@mui/material";
import SectorDropdown from "../../../shared/forms/SectorDropDown";
import StateDropdown from "../../../shared/forms/StateProvinceDropDown";
import { BRAND_BLUE, BRAND_GREEN } from "../../../theme/colors";

export default function BRESurveyForm({
  normQuestions,
  responses,
  setAnswer,
  companyName,
  setCompanyName,
  sector,
  setSector,
  naicsCode,
  setNaicsCode,
  companyPhone,
  setCompanyPhone,
  companyWebsite,
  setCompanyWebsite,
  companyCity,
  setCompanyCity,
  companyState,
  setCompanyState,
  handleSubmit,
  submitDisabled,
  submitButtonSx,
}) {
  return (
    <>
      <Stack spacing={3} mb={4} mt={2}>
        <TextField
          label="Company Name"
          fullWidth
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />

        <TextField
          label="NAICS Code (optional)"
          fullWidth
          value={naicsCode}
          onChange={(e) => setNaicsCode(e.target.value)}
          helperText="Enter your code if known. It will determine the industry and sector when submitted."
        />

        <SectorDropdown
          label="Sector"
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          sx={{
            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
              color: BRAND_BLUE,
            },
            "& .MuiSelect-select, & .MuiInputBase-input": {
              color: `${BRAND_BLUE} !important`,
            },
            "& .MuiSvgIcon-root": { color: BRAND_BLUE },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: { "& .MuiMenuItem-root": { color: BRAND_BLUE } },
              },
            },
          }}
        />

        <TextField
          label="Company Phone Number"
          fullWidth
          value={companyPhone}
          onChange={(e) => setCompanyPhone(e.target.value)}
        />

        <TextField
          label="Company Website"
          fullWidth
          type="url"
          value={companyWebsite}
          onChange={(e) => setCompanyWebsite(e.target.value)}
        />

        <TextField
          label="City"
          fullWidth
          value={companyCity}
          onChange={(e) => setCompanyCity(e.target.value)}
        />

        <StateDropdown
          label="State"
          country="United States"
          value={companyState}
          onChange={(e) => setCompanyState(e.target.value)}
          sx={{
            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
              color: BRAND_BLUE,
            },
            "& .MuiSelect-select, & .MuiInputBase-input": {
              color: `${BRAND_BLUE} !important`,
            },
            "& .MuiSvgIcon-root": { color: BRAND_BLUE },
          }}
        />
      </Stack>

      {normQuestions.map((question, index) => (
        <Box key={question.id} mb={4}>
          <Typography variant="h6" gutterBottom sx={{ color: BRAND_BLUE }}>
            {index + 1}. {question.label}
            {question.required && (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: "error.main", ml: 1 }}
              >
                * required
              </Typography>
            )}
          </Typography>

          {question.type === "text" && (
            <TextField
              fullWidth
              value={responses[question.id] || ""}
              onChange={(e) => setAnswer(question.id, e.target.value)}
              placeholder="Type your response..."
            />
          )}

          {question.type === "textarea" && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={responses[question.id] || ""}
              onChange={(e) => setAnswer(question.id, e.target.value)}
              placeholder="Type your response..."
            />
          )}

          {question.type === "single" && (
            <FormControl component="fieldset">
              <RadioGroup
                value={responses[question.id] || ""}
                onChange={(e) => setAnswer(question.id, e.target.value)}
              >
                {question.options.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={String(option)}
                    control={
                      <Radio
                        sx={{
                          color: BRAND_BLUE,
                          "&.Mui-checked": { color: BRAND_GREEN },
                        }}
                      />
                    }
                    label={String(option)}
                    sx={{ "& .MuiFormControlLabel-label": { color: BRAND_BLUE } }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}

          {question.type === "multi" && (
            <Stack spacing={1}>
              {question.options.map((option) => {
                const values = Array.isArray(responses[question.id])
                  ? responses[question.id]
                  : [];
                const checked = values.includes(option);

                return (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAnswer(question.id, [...values, option]);
                          } else {
                            setAnswer(
                              question.id,
                              values.filter((value) => value !== option),
                            );
                          }
                        }}
                        sx={{
                          color: BRAND_BLUE,
                          "&.Mui-checked": { color: BRAND_GREEN },
                        }}
                      />
                    }
                    label={String(option)}
                    sx={{ "& .MuiFormControlLabel-label": { color: BRAND_BLUE } }}
                  />
                );
              })}
            </Stack>
          )}
        </Box>
      ))}

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="outlined"
          onClick={handleSubmit}
          disabled={submitDisabled}
          sx={submitButtonSx}
        >
          Submit
        </Button>
      </Box>
    </>
  );
}
